import { Icon } from "@opendash/icons";
import { Alert, Button, Image, Space, Spin, Tooltip, Typography } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import { useCallback, useEffect, useMemo, useState } from "react";

type Props = {
  directory?: string;
  imageUrls?: string[];
  title?: string;
  height?: number;
};

type ImageResponse = {
  previewImages?: string[];
  filenames?: string[];
};

type ImageFileResponse = {
  data: string;
  mimeType: string;
  filename: string;
};

type ImageEntry = {
  preview: string;
  filename?: string;
  url?: string;
};

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export const ImageGallery = observer((props: Props) => {
  const { directory, imageUrls, title, height = 150 } = props;
  const [loading, setLoading] = useState(() => Boolean(directory?.trim()));
  const [images, setImages] = useState<ImageEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [failedRequestKey, setFailedRequestKey] = useState<string | null>(null);
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const safeImageUrls = useMemo(() => imageUrls ?? [], [imageUrls]);
  const normalizedDirectory = useMemo(
    () => (typeof directory === "string" ? directory.trim() : ""),
    [directory],
  );
  const requestKey = useMemo(
    () =>
      normalizedDirectory
        ? `directory:${normalizedDirectory}`
        : `urls:${safeImageUrls.join("|")}`,
    [normalizedDirectory, safeImageUrls],
  );

  useEffect(() => {
    let isActive = true;

    const fetchImages = async () => {
      if (!normalizedDirectory) {
        setImages(safeImageUrls.map((url) => ({ preview: url, url })));
        setError(null);
        setFailedRequestKey(null);
        return;
      }

      if (failedRequestKey === requestKey) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = (await Parse.Cloud.run("siv-get-image", {
          directory: normalizedDirectory,
        })) as ImageResponse;

        if (!isActive) {
          return;
        }

        const previews = Array.isArray(response?.previewImages)
          ? response.previewImages.filter(
              (v): v is string => typeof v === "string",
            )
          : [];
        const filenames = Array.isArray(response?.filenames)
          ? response.filenames.filter((v): v is string => typeof v === "string")
          : [];

        const maxLength = Math.max(previews.length, filenames.length);
        const entries: ImageEntry[] = Array.from(
          { length: maxLength },
          (_, i) => ({
            preview: previews[i] ?? "",
            filename: filenames[i],
          }),
        ).filter((e) => e.preview);

        setImages(entries);
        setFailedRequestKey(null);
      } catch (cloudError) {
        if (!isActive) {
          return;
        }

        const message =
          cloudError instanceof Error
            ? cloudError.message
            : "Bilder konnten nicht geladen werden.";
        setError(message);
        setImages([]);
        setFailedRequestKey(requestKey);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchImages();

    return () => {
      isActive = false;
    };
  }, [failedRequestKey, normalizedDirectory, requestKey, safeImageUrls]);

  const downloadSingle = useCallback(
    async (image: ImageEntry): Promise<void> => {
      if (image.url) {
        const anchor = document.createElement("a");
        anchor.href = image.url;
        anchor.download = image.url.split("/").pop() ?? "image";
        anchor.target = "_blank";
        anchor.click();
        return;
      }

      if (image.filename && normalizedDirectory) {
        const response = (await Parse.Cloud.run("siv-get-image-file", {
          directory: normalizedDirectory,
          filename: image.filename,
        })) as ImageFileResponse;

        const byteArray = Uint8Array.from(atob(response.data), (c) =>
          c.charCodeAt(0),
        );
        const blob = new Blob([byteArray], { type: response.mimeType });
        triggerDownload(blob, response.filename);
      }
    },
    [normalizedDirectory],
  );

  const handleDownload = useCallback(
    async (image: ImageEntry, index: number) => {
      setDownloadingIndex(index);
      try {
        await downloadSingle(image);
      } finally {
        setDownloadingIndex(null);
      }
    },
    [downloadSingle],
  );

  const handleDownloadAll = useCallback(async () => {
    setDownloadingAll(true);
    try {
      for (const image of images) {
        await downloadSingle(image);
      }
    } finally {
      setDownloadingAll(false);
    }
  }, [downloadSingle, images]);

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        {title && <Typography.Text strong>{title}</Typography.Text>}
        {images.length > 0 && (
          <Button
            size="small"
            icon={<Icon icon="fa:download" />}
            loading={downloadingAll}
            disabled={downloadingIndex !== null}
            onClick={handleDownloadAll}
          >
            Alle herunterladen
          </Button>
        )}
      </div>

      {error && (
        <Alert
          type="warning"
          showIcon
          message="Bilder konnten nicht geladen werden"
          description={error}
          style={{ marginBottom: 8 }}
        />
      )}

      {loading && (
        <Space style={{ marginBottom: 8 }}>
          <Spin size="small" />
          <Typography.Text type="secondary">
            Bilder werden geladen...
          </Typography.Text>
        </Space>
      )}

      {(!normalizedDirectory && safeImageUrls.length === 0) ||
      (!loading && images.length === 0 && !error) ? (
        <Typography.Text type="secondary">
          Keine Bilder vorhanden.
        </Typography.Text>
      ) : null}

      <Image.PreviewGroup>
        <div
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            paddingBottom: 8,
          }}
        >
          {images.map((image, index) => (
            <div
              key={`${image.filename ?? image.url}-${index}`}
              style={{ position: "relative", flexShrink: 0 }}
            >
              <Image
                src={image.preview}
                alt={`Bild ${index + 1}`}
                preview={{ mask: "Anzeigen" }}
                style={{
                  height: `${height}px`,
                  width: "auto",
                  objectFit: "cover",
                  display: "block",
                  cursor: "pointer",
                  borderRadius: 4,
                }}
              />
              <Tooltip title="Herunterladen">
                <Button
                  icon={<Icon icon="fa:download" />}
                  size="small"
                  loading={downloadingIndex === index}
                  disabled={downloadingAll}
                  onClick={() => handleDownload(image, index)}
                  style={{
                    position: "absolute",
                    bottom: 6,
                    right: 6,
                    opacity: 0.85,
                  }}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      </Image.PreviewGroup>
    </div>
  );
});
